import { useState } from "react";
import { Phone, Mail, MapPin, Navigation, Loader2, Clock } from "lucide-react";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/layout/Breadcrumbs";
import api from "@/lib/api";
import { useSite, telLink, waLink } from "@/context/SiteContext";

export default function Contact() {
  const s = useSite();
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) return toast.error("Please enter your name and phone.");
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent! We'll get back to you soon.");
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch { toast.error("Could not send. Please call us at " + s.phone); }
    finally { setLoading(false); }
  };

  const mapsUrl = s.maps_link || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(s.address)}`;
  const inputCls = "w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none transition-all";

  return (
    <>
      <Seo title="Contact Chouhan Taxi Service | Jodhpur Taxi Booking"
        description="Contact Chouhan Taxi Service in Jodhpur. Call 099829 03736, message on WhatsApp or visit us at Mehrangarh Fort Road, Jodhpur, Rajasthan 342024."
        path="/contact" />
      <Breadcrumbs items={[{ label: "Contact" }]} />
      <section className="pt-8">
        <div className="container-x max-w-3xl">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-extrabold text-navy-heading leading-tight">Contact Us</h1>
          <p className="mt-4 text-slate-600 leading-relaxed">Get in touch to book a taxi or plan your Rajasthan tour. We're available 24/7 to help with your travel.</p>
        </div>
      </section>
      <section className="section-pad pt-10">
        <div className="container-x grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div>
            <div className="bg-navy rounded-2xl p-7 text-white">
              <h2 className="font-heading font-bold text-2xl mb-1">Chouhan Taxi Service</h2>
              <p className="text-brand-sky text-sm font-semibold uppercase tracking-wider mb-5">Tour & Travel</p>
              <div className="space-y-4 text-sm">
                <a href={telLink(s)} className="flex items-start gap-3 hover:text-brand-sky" data-testid="contact-phone"><Phone className="w-5 h-5 text-brand-sky mt-0.5" /><div><div className="text-slate-400">Phone</div><div className="font-semibold">{s.phone}</div></div></a>
                <a href={`mailto:${s.email}`} className="flex items-start gap-3 hover:text-brand-sky"><Mail className="w-5 h-5 text-brand-sky mt-0.5" /><div><div className="text-slate-400">Email</div><div className="font-semibold">{s.email}</div></div></a>
                <div className="flex items-start gap-3"><MapPin className="w-5 h-5 text-brand-sky mt-0.5" /><div><div className="text-slate-400">Address</div><div className="font-semibold">{s.address}</div></div></div>
                <div className="flex items-start gap-3"><Clock className="w-5 h-5 text-brand-sky mt-0.5" /><div><div className="text-slate-400">Hours</div><div className="font-semibold">Open 24 hours · 7 days a week</div></div></div>
              </div>
              <div className="mt-6 grid grid-cols-2 gap-3">
                <a href={telLink(s)} className="flex items-center justify-center gap-2 bg-white text-navy font-semibold py-2.5 rounded-full text-sm" data-testid="contact-call-btn"><Phone className="w-4 h-4" /> Call Now</a>
                <a href={waLink(s)} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 bg-whatsapp text-white font-semibold py-2.5 rounded-full text-sm" data-testid="contact-whatsapp-btn"><WhatsAppIcon className="w-4 h-4" /> WhatsApp</a>
                <a href={mapsUrl} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 border border-white/40 text-white font-semibold py-2.5 rounded-full text-sm" data-testid="contact-directions-btn"><Navigation className="w-4 h-4" /> Directions</a>
                <a href="/book-taxi" className="flex items-center justify-center gap-2 bg-brand text-white font-semibold py-2.5 rounded-full text-sm">Book a Taxi</a>
              </div>
            </div>
            <div className="mt-6 rounded-2xl overflow-hidden border border-slate-200 h-64">
              <iframe title="Chouhan Taxi Service location" width="100%" height="100%" style={{ border: 0 }} loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                src={s.maps_embed || `https://maps.google.com/maps?q=${encodeURIComponent(s.address)}&output=embed`} />
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-100 shadow-lg p-7">
            <h2 className="font-heading font-bold text-navy-heading text-xl mb-5">Send Us a Message</h2>
            <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
              <input className={inputCls} placeholder="Your name *" value={form.name} onChange={set("name")} data-testid="contact-name-input" />
              <input className={inputCls} placeholder="Phone number *" value={form.phone} onChange={set("phone")} data-testid="contact-phone-input" />
              <input className={inputCls} placeholder="Email (optional)" value={form.email} onChange={set("email")} data-testid="contact-email-input" />
              <textarea rows={5} className={inputCls} placeholder="How can we help you?" value={form.message} onChange={set("message")} data-testid="contact-message-input" />
              <button disabled={loading} className="w-full flex items-center justify-center gap-2 bg-brand hover:bg-royal text-white font-semibold py-3.5 rounded-full transition-all active:scale-[0.98] disabled:opacity-70" data-testid="contact-submit-btn">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : null} {loading ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}
